import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, isSuperAdmin } from '@/stores/authStore';
import api from '@/lib/api';
import type { User } from '@/types';
import AuthLayout from '@/layouts/AuthLayout';
import AdminLayout from '@/layouts/AdminLayout';
import { DashboardLayout } from '@/components/dashboard';
import DownloadIndicator from '@/components/DownloadIndicator';
import LoginPage from '@/pages/auth/LoginPage';
import RegisterPage from '@/pages/auth/RegisterPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import ConfirmAccountPage from '@/pages/auth/ConfirmAccountPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
import DashboardPage from '@/pages/DashboardPage';
import InvoicesPage from '@/pages/invoices/InvoicesPage';
import InvoiceDetailPage from '@/pages/invoices/InvoiceDetailPage';
import InvoiceCreatePage from '@/pages/invoices/InvoiceCreatePage';
import QuotesPage from '@/pages/quotes/QuotesPage';
import QuoteCreatePage from '@/pages/quotes/QuoteCreatePage';
import QuoteDetailPage from '@/pages/quotes/QuoteDetailPage';
import QuoteAcceptPage from '@/pages/quotes/QuoteAcceptPage';
import ExpensesPage from '@/pages/expenses/ExpensesPage';
import ExpenseCreatePage from '@/pages/expenses/ExpenseCreatePage';
import ClientsPage from '@/pages/clients/ClientsPage';
import ClientDetailPage from '@/pages/clients/ClientDetailPage';
import BankPage from '@/pages/bank/BankPage';
import DocumentsPage from '@/pages/documents/DocumentsPage';
import VatReportsPage from '@/pages/reports/VatReportsPage';
import SettingsPage from '@/pages/settings/SettingsPage';
import ApiKeysPage from '@/pages/settings/ApiKeysPage';
import ApiDocsPage from '@/pages/settings/ApiDocsPage';
import TermsAndConditionsEditor from '@/pages/settings/TermsAndConditionsEditor';
import TeamPage from '@/pages/team/TeamPage';
import SubscriptionPage from '@/pages/subscription/SubscriptionPage';
import EmailsPage from '@/pages/emails/EmailsPage';
import OverviewPage from '@/pages/admin/OverviewPage';
import AdminUsersPage from '@/pages/admin/UsersPage';
import AdminUserDetailPage from '@/pages/admin/UserDetailPage';
import AdminTicketsPage from '@/pages/admin/TicketsPage';
import AdminTicketDetailPage from '@/pages/admin/TicketDetailPage';
import ActivityPage from '@/pages/admin/ActivityPage';
import ErrorsPage from '@/pages/admin/ErrorsPage';
import HotLeadsPage from '@/pages/admin/HotLeadsPage';
import SubscriptionsPage from '@/pages/admin/SubscriptionsPage';
import PlanPricingPage from '@/pages/admin/PlanPricingPage';
import AlertsPage from '@/pages/admin/AlertsPage';
import SupportPage from '@/pages/support/SupportPage';
import NewTicketPage from '@/pages/support/NewTicketPage';
import SupportTicketDetailPage from '@/pages/support/TicketDetailPage';
import TermsPage from '@/pages/TermsPage';
import LandingPage from '@/pages/LandingPage';
import { usePageTracking } from '@/hooks/usePageTracking';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdmin(user)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const SUPPORTED_LANGS = ['en', 'fr', 'nl', 'es'];

// Root route: landing page for guests, dashboard for authenticated users.
// Accepts an optional /:lang segment (e.g. /fr) so promo links can target a locale directly.
function HomeRoute() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem('ledgora-lang', lang);
    }
  }, [lang, i18n]);

  if (!isAuthenticated) return <LandingPage />;
  return (
    <DashboardLayout>
      <DashboardPage />
    </DashboardLayout>
  );
}

export default function App() {
  usePageTracking();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);

  // Refresh cached user (roles, profile) from /me on app boot to pick up
  // server-side role changes (e.g. ROLE_SUPER_ADMIN promotion).
  useEffect(() => {
    if (!isAuthenticated) return;
    api
      .get<User>('/auth/me')
      .then((res) => setUser(res.data))
      .catch(() => {
        // 401 handled by axios interceptor; ignore other errors silently
      });
  }, [isAuthenticated, setUser]);

  return (
    <>
      <DownloadIndicator />
      <Routes>
      {/* Auth routes */}
      <Route element={<GuestRoute><AuthLayout /></GuestRoute>}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/confirm-account" element={<ConfirmAccountPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>

      {/* Admin routes */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<OverviewPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
        <Route path="/admin/tickets" element={<AdminTicketsPage />} />
        <Route path="/admin/tickets/:id" element={<AdminTicketDetailPage />} />
        <Route path="/admin/activity" element={<ActivityPage />} />
        <Route path="/admin/errors" element={<ErrorsPage />} />
        <Route path="/admin/hot-leads" element={<HotLeadsPage />} />
        <Route path="/admin/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/admin/plan-pricing" element={<PlanPricingPage />} />
        <Route path="/admin/alerts" element={<AlertsPage />} />
      </Route>

      {/* Root: landing page (guests) or dashboard (authenticated); /:lang for direct-to-locale promo links */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="/:lang" element={<HomeRoute />} />

      {/* App routes */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/new" element={<InvoiceCreatePage />} />
        <Route path="/invoices/:id/edit" element={<InvoiceDetailPage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/quotes/new" element={<QuoteCreatePage />} />
        <Route path="/quotes/:id/edit" element={<QuoteDetailPage />} />
        <Route path="/quotes/:id/copy" element={<QuoteDetailPage />} />
        <Route path="/quotes/:id" element={<QuoteDetailPage />} />
        <Route path="/expenses" element={<ExpensesPage />} />
        <Route path="/expenses/new" element={<ExpenseCreatePage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/bank" element={<BankPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/emails" element={<EmailsPage />} />
        <Route path="/reports/vat" element={<VatReportsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/api-keys" element={<ApiKeysPage />} />
        <Route path="/settings/api-keys/docs" element={<ApiDocsPage />} />
        <Route path="/settings/terms" element={<TermsAndConditionsEditor />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/new" element={<NewTicketPage />} />
        <Route path="/support/tickets/:id" element={<SupportTicketDetailPage />} />
        <Route path="/terms" element={<TermsPage />} />
      </Route>

      {/* Public routes (no auth required) */}
      <Route path="/quotes/:id/accept" element={<QuoteAcceptPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
